import { AlertTriangle, Check, Circle, Loader2 } from 'lucide-react';

import {
    getNumberValue,
    getStringValue,
    isRecord,
} from '@apps/web/components/phoebe_chat/record_utils';
import {
    TEMPLATE_CARD_BODY_COLUMN_CLASS,
    TEMPLATE_CARD_CLASS,
    TEMPLATE_CARD_END_CLASS,
    TEMPLATE_CARD_HEADER_CLASS,
    TEMPLATE_CARD_SUBTITLE_CLASS,
    TEMPLATE_CARD_TITLE_CLASS,
    TEMPLATE_OUTLINE_BUTTON_XS_CLASS,
} from '@apps/web/components/phoebe_chat_view/dynamic_widgets/template_theme';
import {
    DetailChipList,
    formatDetailStatus,
    formatToolFieldLabel,
    getStringArray,
    ToolDetailHeader,
    ToolDetailKeyValue,
    ToolTopicSection,
} from '@apps/web/components/phoebe_chat_view/tool_calls/tool_call_template';
import {
    buildCsv,
    CSV_BOM,
    reportFilename,
    triggerBlobDownload,
} from '@apps/web/lib/tabular_export';
import { cn } from '@apps/web/lib/utils';

export type ExecutionPlanChecklistStatus =
    | 'pending'
    | 'in_progress'
    | 'completed'
    | 'error';

export type ExecutionPlanChecklistItem = {
    readonly id: string;
    readonly title: string;
    readonly status: ExecutionPlanChecklistStatus;
    readonly subtitle?: string | null;
};

export type NormalizedDeclaredExecutionPlanItemStatus =
    | 'pending'
    | 'in_progress'
    | 'completed';

export type NormalizedDeclaredExecutionPlanFanInState =
    | 'waiting'
    | 'in_progress'
    | 'completed';

export type NormalizedDeclaredExecutionPlanItem = {
    readonly id: string;
    readonly title: string;
    readonly status: NormalizedDeclaredExecutionPlanItemStatus;
    readonly fanInState?: NormalizedDeclaredExecutionPlanFanInState;
};

export function normalizeDeclaredExecutionPlanTitle(
    title: string,
): string | null {
    const normalizedTitle = title
        .trim()
        .replace(/[_-]+/gu, ' ')
        .replace(/\s+/gu, ' ');
    const lowerTitle = normalizedTitle.toLocaleLowerCase();

    if (
        lowerTitle.match(/\b(load|loading|loaded)\b.*\bskills?\b/u) ||
        lowerTitle.match(/\b(view|viewing)\b.*\bskills?\b/u) ||
        lowerTitle.match(
            /\b(check|checking|checked|get|getting|got|retrieve|retrieving|retrieved|fetch|fetching|fetched|look up|looking up|looked up)\b.*\b(current (date and time|datetime|time)|date and time)\b/u,
        ) ||
        lowerTitle.match(/\bupdate\b.*\bexecution plan\b/u) ||
        lowerTitle.match(
            /\b(compose|draft|drafting|write|writing)\b.*\b(response|reply|answer)\b/u,
        ) ||
        lowerTitle.match(
            /\b(save|saving|saved|record|recording|recorded|capture|capturing|captured|file|filing|filed)\b.*\b(notes?|memory)\b/u,
        ) ||
        lowerTitle.match(/\b(use|using|used)\b.*\bnotes?\b/u) ||
        lowerTitle.match(/\b(planning next steps|finalizing response)\b/u)
    ) {
        return null;
    }

    if (lowerTitle.includes('caregiver') || lowerTitle.includes('caregivers')) {
        if (lowerTitle.includes('availab')) {
            return 'Check caregiver availability';
        }
        if (
            lowerTitle.match(
                /\b(find|finding|search|searching|identify|identifying|eligible|recommend|recommendation|suggestion)\b/u,
            )
        ) {
            return 'Find caregivers';
        }
        if (lowerTitle.match(/\b(review|details?|profile)\b/u)) {
            return 'Review caregivers';
        }
    }

    if (lowerTitle.includes('outreach') || lowerTitle.includes('callout')) {
        if (lowerTitle.match(/\b(send|sending|contact|message)\b/u)) {
            return 'Send outreach';
        }
        if (lowerTitle.match(/\b(create|creating|draft|launch|start)\b/u)) {
            return 'Create outreach';
        }
        if (lowerTitle.match(/\b(review|state|status|details?|monitor)\b/u)) {
            return 'Review outreach';
        }
    }

    if (lowerTitle.includes('shift') || lowerTitle.includes('shifts')) {
        if (lowerTitle.match(/\b(fill|filling|assign|assigning)\b/u)) {
            return 'Fill shifts';
        }
        if (lowerTitle.match(/\b(find|finding|search|searching|open)\b/u)) {
            return 'Find shifts';
        }
        if (lowerTitle.match(/\b(review|details?|load|loading)\b/u)) {
            return 'Review shifts';
        }
    }

    if (lowerTitle.includes('client') || lowerTitle.includes('clients')) {
        if (lowerTitle.match(/\b(find|finding|search|searching)\b/u)) {
            return 'Find clients';
        }
        if (lowerTitle.match(/\b(review|details?|profile)\b/u)) {
            return 'Review clients';
        }
    }

    return normalizedTitle;
}

export function normalizeDeclaredExecutionPlanItems(
    rawItems: ReadonlyArray<unknown>,
    makeId: (item: Record<string, unknown>, index: number) => string,
    getTitle: (
        item: Record<string, unknown>,
        index: number,
    ) => string | null = (item) =>
        typeof item['title'] === 'string' ? item['title'] : null,
): Array<NormalizedDeclaredExecutionPlanItem> {
    const items: Array<NormalizedDeclaredExecutionPlanItem> = [];
    let hiddenInProgress = false;
    for (let idx = 0; idx < rawItems.length; idx += 1) {
        const raw = rawItems[idx];
        if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
            continue;
        }
        const item = raw as Record<string, unknown>;
        const title = getTitle(item, idx);
        const status = normalizeDeclaredExecutionPlanStatus(item['status']);
        if (typeof title !== 'string' || title.trim().length === 0) {
            continue;
        }
        const displayTitle = normalizeDeclaredExecutionPlanTitle(title);
        if (displayTitle === null) {
            if (status === 'in_progress') {
                hiddenInProgress = true;
            }
            continue;
        }
        const displayStatus =
            hiddenInProgress && status === 'pending' ? 'in_progress' : status;
        if (displayStatus === 'in_progress') {
            hiddenInProgress = false;
        }
        items.push({
            id: makeId(item, idx),
            title: displayTitle,
            status: displayStatus,
            fanInState: normalizeDeclaredExecutionPlanFanInState(item),
        });
    }
    return items;
}

export type ParsedStructuredReport = {
    readonly columns: ReadonlyArray<string>;
    readonly rows: ReadonlyArray<ReadonlyArray<string>>;
    readonly summary: {
        readonly returned: number;
        readonly truncated: boolean;
        readonly filters: Record<string, unknown>;
    };
};

export function CurrentDatetimeCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const currentTime =
        getStringValue(data, 'current_time') ??
        getStringValue(data, 'current_datetime') ??
        getStringValue(data, 'datetime') ??
        getStringValue(data, 'now');
    const timezone = getStringValue(data, 'timezone');

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title="Current datetime"
                subtitle={timezone}
                badges={timezone ? [{ label: timezone, tone: 'neutral' }] : []}
            />
            <ToolTopicSection title="Time">
                <p className="text-foreground text-xs">
                    {currentTime ?? 'Not returned'}
                </p>
            </ToolTopicSection>
        </div>
    );
}

export function ExecutionPlanUpdateCard({
    args,
}: {
    readonly args: Record<string, unknown> | null;
    readonly data: Record<string, unknown>;
}) {
    const rawItems = Array.isArray(args?.['items']) ? args['items'] : [];
    const checklistItems = normalizeDeclaredExecutionPlanItems(
        rawItems,
        (item, index) =>
            getStringValue(item, 'id') ?? `execution-plan-tool-${index}`,
        (item, index) => getStringValue(item, 'title') ?? `Step ${index + 1}`,
    );

    return (
        <ExecutionPlanChecklist
            items={checklistItems}
            emptyText="No steps returned"
        />
    );
}

export function ExecutionPlanChecklist({
    items,
    title = 'Execution plan',
    emptyText = 'No steps yet',
    className,
}: {
    readonly items: ReadonlyArray<ExecutionPlanChecklistItem>;
    readonly title?: string;
    readonly emptyText?: string;
    readonly className?: string;
}) {
    return (
        <section
            data-testid="execution-plan-checklist"
            className={cn('max-w-md text-left', className)}
        >
            <div className="text-muted-foreground mb-2 text-xs font-medium">
                {title}
            </div>
            {items.length === 0 ? (
                <p className="text-muted-foreground text-xs">{emptyText}</p>
            ) : (
                <ol className="space-y-1.5">
                    {items.map((item) => (
                        <ExecutionPlanChecklistRow key={item.id} item={item} />
                    ))}
                </ol>
            )}
        </section>
    );
}

function ExecutionPlanChecklistRow({
    item,
}: {
    readonly item: ExecutionPlanChecklistItem;
}) {
    return (
        <li
            data-testid={`execution-plan-item-${item.id}`}
            data-status={item.status}
            className="flex items-start gap-2 text-xs leading-relaxed"
        >
            <ExecutionPlanChecklistIcon status={item.status} />
            <div className="min-w-0 flex-1">
                <div
                    className={cn(
                        'break-words',
                        item.status === 'pending'
                            ? 'text-muted-foreground'
                            : 'text-foreground',
                        item.status === 'in_progress' ? 'font-medium' : null,
                    )}
                >
                    {item.title}
                </div>
                {item.subtitle ? (
                    <div className="text-muted-foreground mt-0.5 break-words">
                        {item.subtitle}
                    </div>
                ) : null}
            </div>
        </li>
    );
}

function ExecutionPlanChecklistIcon({
    status,
}: {
    readonly status: ExecutionPlanChecklistStatus;
}) {
    if (status === 'completed') {
        return (
            <Check
                className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-green-600"
                aria-label="Completed"
            />
        );
    }
    if (status === 'in_progress') {
        return (
            <Loader2
                className="text-primary mt-0.5 h-3.5 w-3.5 flex-shrink-0 animate-spin"
                aria-label="In progress"
            />
        );
    }
    if (status === 'error') {
        return (
            <AlertTriangle
                className="mt-0.5 h-3.5 w-3.5 flex-shrink-0 text-amber-700"
                aria-label="Needs review"
            />
        );
    }
    return (
        <Circle
            className="text-muted-foreground/60 mt-0.5 h-3.5 w-3.5 flex-shrink-0"
            aria-label="Pending"
        />
    );
}

function normalizeDeclaredExecutionPlanStatus(
    status: unknown,
): NormalizedDeclaredExecutionPlanItemStatus {
    if (status === 'completed' || status === 'in_progress') {
        return status;
    }
    return 'pending';
}

function normalizeDeclaredExecutionPlanFanInState(
    item: Record<string, unknown>,
): NormalizedDeclaredExecutionPlanFanInState | undefined {
    if (item['waiting_on_fan_in'] === true) {
        return 'waiting';
    }

    const metadata = isRecord(item['metadata']) ? item['metadata'] : null;
    const rawState =
        item['fan_in_state'] ??
        item['fanInState'] ??
        metadata?.['fan_in_state'];
    if (typeof rawState !== 'string') {
        return undefined;
    }

    const normalizedState = rawState.trim().toLocaleLowerCase();
    if (normalizedState === 'completed' || normalizedState === 'done') {
        return 'completed';
    }
    if (
        normalizedState === 'in_progress' ||
        normalizedState === 'running' ||
        normalizedState === 'active'
    ) {
        return 'in_progress';
    }
    if (normalizedState === 'waiting' || normalizedState === 'pending') {
        return 'waiting';
    }
    return undefined;
}

export function humanizeReportToolName(toolName: string): string {
    const titles: Record<string, string> = {
        query_outreach_report: 'Outreach report',
        generate_report: 'Report',
    };
    if (titles[toolName]) {
        return titles[toolName];
    }
    return toolName
        .replace(/^query_/, '')
        .split('_')
        .map((part) =>
            part.length > 0 ? part[0]!.toUpperCase() + part.slice(1) : part,
        )
        .join(' ');
}

export function StructuredReportSummaryCard({
    toolName,
    report,
}: {
    readonly toolName: string;
    readonly report: ParsedStructuredReport;
}) {
    const filters = Object.entries(report.summary.filters)
        .filter(([, value]) => value !== null && value !== undefined)
        .map(
            ([key, value]) => `${formatToolFieldLabel(key)}: ${String(value)}`,
        );

    function handleDownloadCsv() {
        const blob = new Blob(
            [CSV_BOM + buildCsv(report.columns, report.rows)],
            { type: 'text/csv;charset=utf-8' },
        );
        triggerBlobDownload(
            blob,
            reportFilename(toolName.replace(/^query_/, ''), 'csv'),
        );
    }

    const visibleRows = report.rows.slice(0, 5);

    return (
        <div className={TEMPLATE_CARD_CLASS}>
            <div className={TEMPLATE_CARD_HEADER_CLASS}>
                <div className="min-w-0">
                    <p className={TEMPLATE_CARD_TITLE_CLASS}>
                        {humanizeReportToolName(toolName)}
                    </p>
                    <p className={TEMPLATE_CARD_SUBTITLE_CLASS}>
                        {filters.length > 0
                            ? filters.join(' · ')
                            : `${report.summary.returned} row${report.summary.returned === 1 ? '' : 's'}`}
                    </p>
                </div>
                <div className={TEMPLATE_CARD_END_CLASS}>
                    <button
                        type="button"
                        className={TEMPLATE_OUTLINE_BUTTON_XS_CLASS}
                        onClick={handleDownloadCsv}
                    >
                        Export CSV
                    </button>
                </div>
            </div>
            <div className={TEMPLATE_CARD_BODY_COLUMN_CLASS}>
                <div className="grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-2">
                    <div className="rounded-[10px] border border-[#E9F0F0] bg-[#F7FAFA] px-3 py-2.5">
                        <p className="text-[11px] font-medium tracking-[0.04em] text-[#617578] uppercase">
                            Rows
                        </p>
                        <p className="mt-0.5 text-[22px] font-bold tracking-[-0.02em] text-[#002429] tabular-nums">
                            {report.summary.returned}
                        </p>
                        {report.summary.truncated ? (
                            <p className="text-[11px] text-[#8C3B00]">
                                Result is truncated
                            </p>
                        ) : null}
                    </div>
                    <div className="rounded-[10px] border border-[#E9F0F0] bg-[#F7FAFA] px-3 py-2.5">
                        <p className="text-[11px] font-medium tracking-[0.04em] text-[#617578] uppercase">
                            Columns
                        </p>
                        <p className="mt-0.5 text-[22px] font-bold tracking-[-0.02em] text-[#002429] tabular-nums">
                            {report.columns.length}
                        </p>
                    </div>
                </div>
                <div className="max-w-full overflow-x-auto">
                    <table className="w-full border-collapse text-[13px]">
                        <thead>
                            <tr>
                                {report.columns.map((column) => (
                                    <th
                                        key={column}
                                        className="border-b border-[#DBE3E3] p-2 text-left font-medium whitespace-nowrap text-[#002429]"
                                    >
                                        {column}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {visibleRows.map((row, rowIndex) => (
                                <tr key={`report-row-${rowIndex}`}>
                                    {report.columns.map(
                                        (column, columnIndex) => (
                                            <td
                                                key={`${column}-${columnIndex}`}
                                                className="border-b border-[#E9F0F0] p-2 align-middle text-[#244045] last:border-b-0"
                                            >
                                                {row[columnIndex] ?? ''}
                                            </td>
                                        ),
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <p className="text-[11px] text-[#617578]">
                    {report.rows.length > visibleRows.length
                        ? `Showing ${visibleRows.length} of ${report.rows.length} rows. Open the attached report for the full table.`
                        : 'The attached report includes this full table.'}
                </p>
            </div>
        </div>
    );
}

export function OrganizationContextCard({
    data,
}: {
    readonly data: Record<string, unknown>;
}) {
    const organization = isRecord(data['organization'])
        ? data['organization']
        : {};
    const counts = isRecord(data['counts']) ? data['counts'] : {};
    const settings = isRecord(data['settings']) ? data['settings'] : {};
    const offices = getStringArray(data['offices']);
    const coordinators = Array.isArray(data['coordinators'])
        ? data['coordinators'].filter(isRecord)
        : [];
    const members = Array.isArray(data['members'])
        ? data['members'].filter(isRecord)
        : [];
    const organizationName =
        getStringValue(organization, 'name') ?? 'Organization';

    return (
        <div className="space-y-2">
            <ToolDetailHeader
                title={organizationName}
                subtitle={getStringValue(organization, 'timezone')}
                badges={[
                    ...(getStringValue(settings, 'assistant_name')
                        ? [
                              {
                                  label: getStringValue(
                                      settings,
                                      'assistant_name',
                                  )!,
                                  tone: 'blue' as const,
                              },
                          ]
                        : []),
                ]}
            />
            <ToolTopicSection title="Record counts">
                <div className="grid grid-cols-3 gap-2">
                    {[
                        ['Clients', getNumberValue(counts, 'active_clients')],
                        [
                            'Caregivers',
                            getNumberValue(counts, 'active_caregivers'),
                        ],
                        [
                            'Coordinators',
                            getNumberValue(counts, 'active_coordinators'),
                        ],
                    ].map(([label, value]) => (
                        <div key={label} className="min-w-0">
                            <p className="text-foreground text-sm font-semibold">
                                {value ?? '—'}
                            </p>
                            <p className="text-muted-foreground text-[10px]">
                                {label}
                            </p>
                        </div>
                    ))}
                </div>
            </ToolTopicSection>
            <ToolTopicSection title="Scheduling settings">
                <div className="space-y-1.5">
                    <ToolDetailKeyValue
                        label="Client term"
                        value={
                            formatDetailStatus(
                                getStringValue(
                                    settings,
                                    'client_reference_term',
                                ),
                            ) ?? 'Not returned'
                        }
                    />
                    <ToolDetailKeyValue
                        label="Routing"
                        value={
                            formatDetailStatus(
                                getStringValue(
                                    settings,
                                    'notification_routing_mode',
                                ),
                            ) ?? 'Not returned'
                        }
                    />
                    <ToolDetailKeyValue
                        label="Week starts"
                        value={
                            formatDetailStatus(
                                getStringValue(settings, 'work_week_start_day'),
                            ) ?? 'Not returned'
                        }
                    />
                </div>
            </ToolTopicSection>
            <ToolTopicSection title="Offices">
                <DetailChipList values={offices.slice(0, 8)} />
            </ToolTopicSection>
            {coordinators.length > 0 ? (
                <ToolTopicSection title="Coordinators">
                    <div className="space-y-1">
                        {coordinators.slice(0, 5).map((coordinator) => (
                            <p
                                key={
                                    getStringValue(
                                        coordinator,
                                        'coordinator_id',
                                    ) ??
                                    getStringValue(coordinator, 'name') ??
                                    'coordinator'
                                }
                                className="text-foreground text-xs"
                            >
                                {getStringValue(coordinator, 'name') ??
                                    'Coordinator'}
                            </p>
                        ))}
                    </div>
                </ToolTopicSection>
            ) : null}
            {members.length > 0 ? (
                <ToolTopicSection title="Users">
                    <p className="text-foreground text-xs">
                        {members.length} user
                        {members.length === 1 ? '' : 's'} returned
                    </p>
                </ToolTopicSection>
            ) : null}
        </div>
    );
}
